/**
 * AI Context Manager
 * Tracks interactions, learns from feedback, builds user profiles
 * Enables the AI to adapt and improve over time
 */

export const aiContextManager = {
  // In-memory storage
  contexts: {}, // { type: [] }
  feedback: [], // Learning data
  userProfiles: {}, // { userId: { patterns, preferences } }
  maxContextSize: 5000, // Max tokens to store

  /**
   * Add context from an interaction
   */
  addContext(type, data, metadata = '') {
    if (!this.contexts[type]) {
      this.contexts[type] = []
    }

    const contextEntry = {
      timestamp: new Date().toISOString(),
      data: data,
      metadata: metadata,
    }

    this.contexts[type].push(contextEntry)

    // Trim old context if too large
    if (this.contexts[type].length > 20) {
      this.contexts[type] = this.contexts[type].slice(-20)
    }

    // Persist to localStorage
    this._persistContexts()
  },

  /**
   * Get recent context of a type
   */
  getRecentContext(type, userId = 'default', limit = 5) {
    const userKey = `${type}_${userId}`
    const entries = this.contexts[userKey] || this.contexts[type] || []
    return entries.slice(-limit)
  },

  /**
   * Record feedback from user for learning
   */
  recordFeedback(interaction, feedback) {
    const feedbackEntry = {
      timestamp: new Date().toISOString(),
      interaction: interaction,
      feedback: feedback,
      rating: feedback.rating || 0.5,
      shouldFineTune: feedback.shouldFineTune || false,
      userId: feedback.userId || 'default',
    }

    this.feedback.push(feedbackEntry)

    // Update user profile based on feedback
    this._updateUserProfile(feedback.userId || 'default', feedback, interaction)

    // Keep recent feedback
    if (this.feedback.length > 100) {
      this.feedback = this.feedback.slice(-100)
    }

    // Persist
    this._persistFeedback()
  },

  /**
   * Get user's learned patterns
   */
  getUserPattern(userId = 'default') {
    return this.userProfiles[userId] || {
      preferredTone: 'professional',
      detailLevel: 'moderate',
      communicationStyle: 'direct',
      responseLength: 'moderate',
      technicalLevel: 'intermediate',
    }
  },

  /**
   * Get context size for monitoring
   */
  getContextSize() {
    let totalSize = 0
    for (const type in this.contexts) {
      totalSize += JSON.stringify(this.contexts[type]).length
    }
    return totalSize
  },

  /**
   * Get feedback count
   */
  getFeedbackCount() {
    return this.feedback.length
  },

  /**
   * Get number of user profiles
   */
  getUserProfileCount() {
    return Object.keys(this.userProfiles).length
  },

  /**
   * Clear all context (privacy control)
   */
  clearAllContext() {
    this.contexts = {}
    this.feedback = []
    this.userProfiles = {}
    localStorage.removeItem('ai_contexts')
    localStorage.removeItem('ai_feedback')
    localStorage.removeItem('ai_user_profiles')
  },

  /**
   * Get learning metrics for dashboard
   */
  getLearningMetrics() {
    return {
      totalInteractions: this.getTotalInteractions(),
      averageRating: this._calculateAverageRating(),
      topTags: this._getTopTags(),
      userProfiles: this.getUserProfileCount(),
      lastUpdated: new Date().toISOString(),
    }
  },

  /**
   * Get all interactions for analysis
   */
  getAllInteractions() {
    return {
      contexts: this.contexts,
      feedback: this.feedback,
      userProfiles: this.userProfiles,
    }
  },

  /**
   * Internal: Update user profile from feedback
   */
  _updateUserProfile(userId, feedback, interaction) {
    if (!this.userProfiles[userId]) {
      this.userProfiles[userId] = {
        interactions: 0,
        preferences: {},
        patterns: {},
        averageRating: 0,
      }
    }

    const profile = this.userProfiles[userId]

    // Update interaction count
    profile.interactions = (profile.interactions || 0) + 1

    // Update preferences if provided
    if (feedback.preferences) {
      profile.preferences = { ...profile.preferences, ...feedback.preferences }
    }

    // Update average rating
    if (feedback.rating !== undefined) {
      const allRatings = this.feedback
        .filter(f => f.userId === userId)
        .map(f => f.rating)
      profile.averageRating = allRatings.reduce((a, b) => a + b, 0) / allRatings.length
    }

    // Learn patterns from interaction
    if (interaction.pattern) {
      profile.patterns = { ...profile.patterns, ...interaction.pattern }
    }

    // Persist
    this._persistProfiles()
  },

  /**
   * Internal: Calculate average rating
   */
  _calculateAverageRating() {
    if (this.feedback.length === 0) return 0
    const sum = this.feedback.reduce((acc, f) => acc + (f.rating || 0), 0)
    return (sum / this.feedback.length).toFixed(2)
  },

  /**
   * Internal: Get top feedback tags
   */
  _getTopTags() {
    const tags = {}
    this.feedback.forEach(f => {
      if (f.feedback.tags) {
        f.feedback.tags.forEach(tag => {
          tags[tag] = (tags[tag] || 0) + 1
        })
      }
    })
    return Object.entries(tags)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([tag, count]) => ({ tag, count }))
  },

  /**
   * Internal: Get total interactions
   */
  getTotalInteractions() {
    return Object.values(this.contexts).reduce((sum, arr) => sum + arr.length, 0)
  },

  /**
   * Internal: Persist contexts to localStorage
   */
  _persistContexts() {
    try {
      localStorage.setItem('ai_contexts', JSON.stringify(this.contexts))
    } catch (error) {
      console.warn('Failed to persist contexts:', error)
    }
  },

  /**
   * Internal: Persist feedback to localStorage
   */
  _persistFeedback() {
    try {
      localStorage.setItem('ai_feedback', JSON.stringify(this.feedback))
    } catch (error) {
      console.warn('Failed to persist feedback:', error)
    }
  },

  /**
   * Internal: Persist user profiles to localStorage
   */
  _persistProfiles() {
    try {
      localStorage.setItem('ai_user_profiles', JSON.stringify(this.userProfiles))
    } catch (error) {
      console.warn('Failed to persist profiles:', error)
    }
  },

  /**
   * Load persisted data from localStorage
   */
  loadPersistedData() {
    try {
      const contexts = localStorage.getItem('ai_contexts')
      const feedback = localStorage.getItem('ai_feedback')
      const profiles = localStorage.getItem('ai_user_profiles')

      if (contexts) this.contexts = JSON.parse(contexts)
      if (feedback) this.feedback = JSON.parse(feedback)
      if (profiles) this.userProfiles = JSON.parse(profiles)

      console.log('[AI Context] Loaded persisted data')
    } catch (error) {
      console.warn('Failed to load persisted data:', error)
    }
  },
}

// Load persisted data on initialization
aiContextManager.loadPersistedData()

export default aiContextManager
